import { __awaiter, __rest } from "tslib";
import fetch, { NetworkException } from './fetch';
import { selectAreaName, selectCollectionName } from '@/store/slices/sessionSlice';
import { getTenantName } from '@/utils/tenant';
import { getStore } from '@/store/store-proxy';
import { getToken, getTokenForced } from '@/auth/auth-access';
import { collectionIdByName } from '@/store/selectors/collections';
import { areaIdByNameAndCollectionIdSelector } from '@/store/selectors/areas';
export const JSON_HEADERS = { 'Content-Type': 'application/json' };
export const buildSessionHeaders = () => {
    const headers = {};
    const store = getStore();
    const state = store.getState();
    const areaName = selectAreaName(state);
    const collectionName = selectCollectionName(state);
    const collectionId = collectionIdByName(collectionName)(state);
    const areaId = areaIdByNameAndCollectionIdSelector(collectionId, areaName)(state);
    if (areaName)
        headers['X-Groupby-Area-Name'] = areaName;
    if (areaId)
        headers['X-Groupby-Area-Id'] = String(areaId);
    if (collectionName)
        headers['X-Groupby-Collection-Name'] = collectionName;
    return headers;
};
export const buildCustomerHeaders = () => ({ 'X-Groupby-Customer-Id': getTenantName() });
export const buildHeaders = () => (Object.assign(Object.assign({}, buildCustomerHeaders()), buildSessionHeaders()));
export function s4rAuthenticated(config) {
    return __awaiter(this, void 0, void 0, function* () {
        const token = yield getToken();
        const { url, method, body: rawBody, files, responseType, headers: rawHeaders = Object.assign({}, buildHeaders()), expiry = true, skipCache, signal, } = config;
        let headers = rawHeaders;
        let body = null;
        if (files) {
            body = yield mapFilesToMultipartBody(files);
        }
        else if (rawBody) {
            headers = Object.assign(Object.assign(Object.assign({}, JSON_HEADERS), buildHeaders()), headers);
            body = typeof rawBody === 'string' ? rawBody : JSON.stringify(rawBody);
        }
        const request = {
            method,
            skipCache,
            expiry,
            responseType,
            headers: Object.assign(Object.assign({}, headers), { Authorization: `Bearer ${token}` }),
            body,
            signal,
        };
        return fetch(url, request)
            .then(extractResponse())
            .then(extractTokens)
            .catch((error) => __awaiter(this, void 0, void 0, function* () {
            if (error instanceof NetworkException && error.statusCode === 401) {
                const freshToken = yield getTokenForced();
                const retryRequest = Object.assign(Object.assign({}, request), { headers: Object.assign(Object.assign({}, request.headers), { Authorization: `Bearer ${freshToken}` }) });
                return fetch(url, retryRequest)
                    .then(extractResponse())
                    .then(extractTokens);
            }
            throw error;
        }));
    });
}
export function authenticated(cb) {
    return (...args) => __awaiter(this, void 0, void 0, function* () {
        const token = yield getToken();
        const { url, method = 'GET', body: rawBody, files, parse, headers: rawHeaders = {}, expiry, skipCache, } = cb(...args);
        let headers = rawHeaders;
        let body = null;
        if (files) {
            headers = Object.keys(headers)
                .filter((key) => key.toLowerCase() !== 'content-type')
                .reduce((filtered, key) => {
                filtered[key] = headers[key];
                return filtered;
            }, {});
            body = yield mapFilesToMultipartBody(files);
        }
        else if (rawBody) {
            headers = Object.assign(Object.assign({}, headers), JSON_HEADERS);
            body = typeof rawBody === 'string' ? rawBody : JSON.stringify(rawBody);
        }
        const request = {
            method,
            skipCache,
            expiry,
            headers: Object.assign(Object.assign({}, headers), { Authorization: `Bearer ${token}` }),
            body,
        };
        return fetch(url, request)
            .then(extractResponse(parse))
            .then(extractTokens)
            .catch((error) => __awaiter(this, void 0, void 0, function* () {
            if (error instanceof NetworkException && error.statusCode === 401) {
                const freshToken = yield getTokenForced();
                const retryRequest = Object.assign(Object.assign({}, request), { headers: Object.assign(Object.assign({}, request.headers), { Authorization: `Bearer ${freshToken}` }) });
                return fetch(url, retryRequest)
                    .then(extractResponse(parse))
                    .then(extractTokens);
            }
            throw error;
        }));
    });
}
export function adminAuthenticated(clientKey, cb) {
    return (...args) => __awaiter(this, void 0, void 0, function* () {
        const { url, headers = {}, expiry, skipCache, } = cb(...args);
        const request = {
            headers,
            skipCache,
            expiry,
            method: 'POST',
            body: JSON.stringify({ clientKey }),
        };
        return fetch(url, request).then(extractResponse());
    });
}
/* eslint-disable @typescript-eslint/require-await */
export function mapFilesToMultipartBody(files) {
    return __awaiter(this, void 0, void 0, function* () {
        const formData = new FormData();
        for (const key of Object.keys(files)) {
            const value = files[key];
            formData.append(key, typeof value === 'boolean' ? String(value) : value);
        }
        return formData;
    });
}
export function extractResponse(parse) {
    return (_a) => __awaiter(this, void 0, void 0, function* () {
        var { result } = _a, response = __rest(_a, ["result"]);
        const rawResult = (result === null || result === void 0 ? void 0 : result.result) || result;
        const parsedResult = parse ? parse(rawResult) : rawResult;
        return Object.assign(Object.assign({}, response), { result: parsedResult });
    });
}
export function extractTokens(response) {
    return __awaiter(this, void 0, void 0, function* () {
        const resultWithTokens = response.result;
        const { access_token: token, refresh_token: refreshToken } = resultWithTokens || {};
        return Object.assign(Object.assign(Object.assign({}, response), (token ? { token } : {})), (refreshToken ? { refreshToken } : {}));
    });
}
/* eslint-enable @typescript-eslint/require-await */
export function arrayParser(parse) {
    return (result) => {
        if (parse) {
            return Array.isArray(result) ? result.map(parse).filter(Boolean) : parse(result);
        }
        return result;
    };
}
export const getEnvSubdomainFromHostname = (hostname) => {
    const [serviceSubdomain, envSubdomain = ''] = hostname.split('.');
    if (serviceSubdomain.includes('-atlas')) {
        return serviceSubdomain;
    }
    return envSubdomain;
};
export const isRequestAborted = (error) => {
    if (error instanceof Error)
        return error.name === 'AbortError';
    if (typeof error === 'object' && error !== null) {
        const obj = error;
        // RTK Query .unwrap() after .abort() throws { name: 'AbortError', message: 'Aborted' }
        if (obj.name === 'AbortError')
            return true;
        // fetchBaseQuery rejection shape: { status: 'FETCH_ERROR', error: 'AbortError: ...' }
        if (obj.status === 'FETCH_ERROR' && typeof obj.error === 'string' && obj.error.includes('AbortError'))
            return true;
    }
    return false;
};
